import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';

const RedeemInvitePage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') || '';

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ role: string } | null>(null);

  const handleRedeem = async () => {
    if (!code) return;
    setSubmitting(true);
    setError(null);
    try {
      const me = await authApi.redeemInvite(code);
      setSuccess({ role: me.role });
      // Force a full reload so AuthContext re-hydrates from /api/auth/me
      // with the freshly granted Membership.
      setTimeout(() => {
        window.location.assign('/');
      }, 1500);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to redeem invite';
      setError(msg);
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (!code) {
    return (
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <h2>Invite link is missing a code</h2>
          <p>This URL doesn't include an invite code. Ask whoever sent it for the full link.</p>
          <Link to="/" className="btn btn-secondary">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = `/redeem-invite?code=${encodeURIComponent(code)}`;
    return (
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <h2>Sign in to redeem your invite</h2>
          <p>You need an account to claim this invite. Log in if you already have one, or sign up first.</p>
          <div className="redeem-actions">
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`)}
            >
              Log in
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/signup?returnUrl=${encodeURIComponent(returnUrl)}`)}
            >
              Sign up
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="form-container auth-form-container">
          <h2>You're in!</h2>
          <p>
            Your invite was accepted as a <strong>{success.role}</strong>. Redirecting…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="form-container auth-form-container">
        <h2>Redeem your invite</h2>
        <p>
          Click the button below to claim your spot on this site. The invite is bound to your
          email and can only be redeemed once.
        </p>

        {error && <p className="auth-error">{error}</p>}

        <div className="redeem-actions">
          <button className="btn btn-primary" onClick={handleRedeem} disabled={submitting}>
            {submitting ? 'Redeeming…' : 'Redeem invite'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RedeemInvitePage;
